import { APIURL, apiGet, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, toggleWidgetTheme, normalizeWidgetTheme, getEChartsTooltipTheme, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

interface PublisherDistributionItem {
    news_publisher: string;
    count: number;
    percentage: number;
}

interface PublisherDetailedBreakdownItem extends PublisherDistributionItem {
    actions: Record<string, number>;
}

interface PublisherAnalysisApiData {
    symbol: string;
    data_found: boolean;
    total_articles: number;
    distribution: PublisherDistributionItem[];
    actions_by_publisher: Record<string, Record<string, number>>;
    all_actions: string[];
    chart_data: {
        labels: string[];
        values: number[];
        percentages: number[];
    };
    detailed_breakdown: PublisherDetailedBreakdownItem[];
}

interface PublisherChartItem {
    name: string;
    value: number;
    count: number;
    percentage: number;
    actions: Record<string, number>;
}

const PUBLISHER_COLORS = [
    '#8B6CFF', '#6FF19A', '#FF2B2B', '#FFA01C', '#44CA6C',
    '#3B82F6', '#EC4899', '#14B8A6', '#F59E0B', '#6366F1'
];

const ACTION_LABELS: Record<string, string> = {
    upgrade: 'Upgrade',
    downgrade: 'Downgrade',
    hold: 'Hold',
    initialise: 'Initialise'
};

export class TraderversePublisherAnalysis {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private currentData: PublisherChartItem[] = [];
    private apiData: PublisherAnalysisApiData | null = null;
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;
    private filters = {
        symbol: '' as string | undefined
    };

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.applyConfigFilters();
        this.container = this.resolveContainer(config.container);
        this.shadowRoot = (this.container as any).shadowRoot || this.container.attachShadow({ mode: 'closed' });
        this.init();
        this.widgetResizeObserver = new ResizeObserver(() => this.updateDisplay());
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        return {
            headerPinned: true,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            isSymbolSelectable: true,
            symbol: 'AAPL'
        };
    }

    private applyConfigFilters(): void {
        if (this.config.symbol) this.filters.symbol = this.config.symbol;
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        await this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.setupChart();
        if (widget) widget.classList.remove('loading');
        cancelSelectEvent(this.shadowRoot);
        this.emit('ready', {});
        this.config.onReady?.();
    }

    private async createWidget(): Promise<void> {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
        <div style="width: ${typeof this.config.width == "number" ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
            <div class="headTop">
                <h4 class="title market">${this.getTitleIconSvg()} Publisher Analysis</h4>
                <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
                    <i class="pi pi-ellipsis-v"></i>
                </div>
            </div>
            <div class="inBody">
                <div class="optionsRow">
                    ${this.config.hideSymbol ? '' : `<div class="item dropStyle01">
                        <div class="trader-sm-card" id="selected-symbol">
                            ${this.config.symbol
                                ? getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol, type: 'stock', name: '' })
                                : '<span>Select Symbol</span>'}
                        </div>
                        <span id="symbol-dropdown-placeholder"></span>
                    </div>`}
                    <span></span>
                </div>
                <div class="chartDv cryptoCompany" id="publisherAnalysisContainer"></div>
            </div>
        </div>`;
        this.shadowRoot.innerHTML = widgetHtml;
        await this.attachStyles();
        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        setupHeaderPin(this.shadowRoot, this.config);

        if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
            const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
            const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
            if (placeholder && dropdown) {
                placeholder.replaceWith(dropdown);
            }
        }
    }

    private onSelectChange(stock: any): void {
        this.changeSymbol(stock.symbol, stock);
    }

    private buildQueryParams(): Record<string, string | number> {
        const query: Record<string, string | number> = {};
        if (this.filters.symbol) query.symbol = this.filters.symbol;
        return query;
    }

    private mapApiData(data: PublisherAnalysisApiData): PublisherChartItem[] {
        const breakdown = data.detailed_breakdown?.length
            ? data.detailed_breakdown
            : (data.distribution ?? []).map((item) => ({
                ...item,
                actions: data.actions_by_publisher?.[item.news_publisher] ?? {}
            }));

        return breakdown.map((item) => ({
            name: item.news_publisher,
            value: item.percentage,
            count: item.count,
            percentage: item.percentage,
            actions: item.actions ?? {}
        }));
    }

    private hasChartData(data: PublisherAnalysisApiData | null): boolean {
        if (!data?.data_found) return false;
        return (data.distribution ?? []).some((item) => item.count > 0);
    }

    private formatActionsTooltip(actions: Record<string, number>): string {
        const entries = Object.entries(actions).filter(([, count]) => count > 0);
        if (!entries.length) return '';
        return entries
            .map(([action, count]) => `<div>${ACTION_LABELS[action] || action}: <b>${count}</b></div>`)
            .join('');
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
            script.async = true;
            script.onload = () => resolve(true);
            this.shadowRoot.appendChild(script);
        });
    }

    private async setupChart(symbol?: string): Promise<void> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const container = this.shadowRoot.querySelector('#publisherAnalysisContainer');
        if (widget) widget.classList.add('loading');

        if (symbol) {
            this.filters.symbol = symbol;
            this.config.symbol = symbol;
        }

        try {
            const res = await apiGet({
                method: 'GET',
                url: `${APIURL}/v1/equity-market/publisher-analysis`,
                query: this.buildQueryParams(),
                headers: { }
            });
            if (!res.ok) throw new Error('Failed to fetch publisher analysis data');

            const json = await res.json();
            const responseData = json?.data as PublisherAnalysisApiData | undefined;
            this.apiData = responseData ?? null;

            if (this.hasChartData(this.apiData)) {
                container?.setAttribute('data-testing-id', 'publisher-analysis-chart');
            } else {
                container?.removeAttribute('data-testing-id');
            }

            if (!this.hasChartData(this.apiData)) {
                this.currentData = [];
                if (container) container.innerHTML = '<div class="noData"><p>No data available</p></div>';
                if (this.currentChart) {
                    this.currentChart.dispose();
                    this.currentChart = null;
                }
            } else {
                this.currentData = this.mapApiData(this.apiData!);
                this.renderChart();
            }

            this.emit('dataUpdate', this.apiData);
        } catch (err) {
            console.error('Failed to fetch/setup chart data:', err);
            this.currentData = [];
            this.apiData = null;
            if (container) container.innerHTML = '<div class="noData"><p>Failed to load data</p></div>';
            this.emit('error', err);
        } finally {
            if (widget) widget.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        if (!this.currentData?.length || !(window as any).echarts) {
            return;
        }
        this.renderChart();
    }

    private renderChart(): void {
        const container = this.shadowRoot.querySelector('#publisherAnalysisContainer');
        if (!container) return;

        container.innerHTML = `<div id="publisherAnalysisChart" class="chart coverDv"></div>`;
        this.drawChart();
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#publisherAnalysisChart') as HTMLElement;
        if (!container || !this.currentData?.length) return;

        if (this.currentChart) {
            this.currentChart.dispose();
        }

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm');
        const bgColor = getComputedStyle(widgetBox).getPropertyValue('--base-dark');

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'item',
                ...getEChartsTooltipTheme(widgetBox),
                formatter: (p: any) => {
                    const actionsHtml = this.formatActionsTooltip(p.data.actions);
                    return `
                  <div style="min-width:160px">
                    <div style="font-weight:600;margin-bottom:6px">
                      <span style="
                        display:inline-block;
                        width:10px;
                        height:10px;
                        border-radius:50%;
                        background:${p.color};
                        margin-right:6px
                      "></span>
                      ${p.name}
                    </div>
                    <div>Articles <b>${p.data.count}</b></div>
                    <div>${p.value.toFixed(2)}% of total</div>
                    ${actionsHtml ? `<div style="margin-top:6px;font-weight:600">Actions</div>${actionsHtml}` : ''}
                  </div>
                `;
                }
            },
            series: [
                {
                    type: 'pie',
                    radius: ['30%', '90%'],
                    center: ['50%', '55%'],
                    startAngle: 90,
                    minAngle: 6,
                    avoidLabelOverlap: true,
                    padAngle: 3,
                    itemStyle: {
                        borderRadius: 8,
                        borderColor: bgColor,
                        borderWidth: 6
                    },
                    label: {
                        show: true,
                        position: 'inside',
                        formatter: '{b}\n{d}%',
                        color: '#FFFFFF',
                        fontSize: fontSize,
                        textBorderColor: "#000000",
                        textBorderWidth: 2,
                        textBorderType: 'solid',
                    },
                    labelLine: { show: false },
                    data: this.currentData.map((d, index) => ({
                        ...d,
                        itemStyle: { color: PUBLISHER_COLORS[index % PUBLISHER_COLORS.length] }
                    }))
                }
            ]
        };

        this.currentChart = echarts.init(container);
        this.currentChart.setOption(option);
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(
            this.shadowRoot,
            this.config,
            stock,
            stockId,
            (symbol: string) => this.setupChart(symbol),
            (type: any, data: any) => this.emit(type, data)
        );
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }

    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;

        this.config.theme = toggleWidgetTheme(
            this.shadowRoot,
            theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

        this.updateDisplay();
    }

    public destroy(): void {
        if (this.currentChart) {
            this.currentChart.dispose();
            this.currentChart = null;
        }
        if (this.widgetResizeObserver) {
            this.widgetResizeObserver.disconnect();
            this.widgetResizeObserver = null;
        }
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) {
            const evt: WidgetEvent = { type, data };
            handlers.forEach(h => h(evt));
        }
    }

    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }
}

declare global { interface Window { TraderversePublisherAnalysis: typeof TraderversePublisherAnalysis; } }
if (typeof window !== 'undefined') window.TraderversePublisherAnalysis = TraderversePublisherAnalysis;
